'use client';

import { useMemo } from 'react';
import LiveWhiteboard, { type DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';
import type { LiveModeSessionOptions, ProblemContext } from '@/components/tutor/live-mode';
import type { BoardEntry, StoredProblem } from '@/components/whiteboard-v2/types';

interface LiveTutorCardProps {
  problem?: StoredProblem;
  selection?: DemoSelection;
  onRequireAuth?: () => void;
  liveSessionOptions?: Pick<LiveModeSessionOptions, 'buildSystemPrompt' | 'initialMessage'>;
  contextOverrides?: Partial<ProblemContext>;
  startSessionSignal?: number;
  previewEntries?: BoardEntry[];
  previewShowBoardHeader?: boolean;
  previewStarStates?: Array<'pending' | 'success' | 'fail'>;
  onBoardStateChange?: (state: {
    entries: BoardEntry[];
    showBoardHeader: boolean;
    starStates: Array<'pending' | 'success' | 'fail'>;
  }) => void;
}

export default function LiveTutorCard({
  problem,
  selection,
  onRequireAuth,
  liveSessionOptions,
  contextOverrides,
  startSessionSignal,
  previewEntries,
  previewShowBoardHeader,
  previewStarStates,
  onBoardStateChange,
}: LiveTutorCardProps) {
  const resolvedSelection = useMemo<DemoSelection | null>(
    () => selection || (problem ? { kind: 'problem', problem } : null),
    [problem, selection],
  );

  if (!resolvedSelection) {
    return null;
  }

  return (
    <div className="w-full py-3">
      <LiveWhiteboard
        selection={resolvedSelection}
        autoStart
        startSessionSignal={startSessionSignal}
        onRequireAuth={onRequireAuth}
        liveSessionOptions={liveSessionOptions}
        contextOverrides={contextOverrides}
        previewEntries={previewEntries}
        previewShowBoardHeader={previewShowBoardHeader}
        previewStarStates={previewStarStates}
        onBoardStateChange={onBoardStateChange}
      />
    </div>
  );
}
